import React, { Component } from "react";
import Loading from "../../common/components/loading/loading";
import {
  getFilteredProductForMarket,
  refilterProductForMarketDateChange,
} from "../../common/services/products/productUtilities";
import { getMarketPriceDriverCombinations } from "../../common/services/markets/marketPriceDriverCombinationService";
import { getVenue } from "../../common/services/markets/venueService";
import {
  getAggregateMarksByMarket,
  getInterpolatedAggregateMarksByMarket,
} from "../../common/services/marketdata/markService";
import { getTermSchedule } from "../../common/services/markets/termScheduleService";
import HistoricalMarketDataTable from "./historicalMarketDataTable";
import { withTranslation } from "react-i18next";
import ListGroup from "react-bootstrap/ListGroup";
import Input from "../../common/components/form/input";
import { HistoricalReportingPeriodicity } from "../../common/enumerations/enums";

class HistoricalMarketDataHeader extends Component {
  _lastDataLoadRequestId = 0;

  state = {
    marketAttributes: [],
    marks: [],
    forwardMarks: [],
    trades: [],
    marketPrices: [],
    isLoading: true,
    marketDataIsLoading: true,
    marketDate: null,
    fromMarketDate: null,
    toMarketDate: null,
    fromDate: null,
    toDate: null,
    termScheduleId: null,
    originalQualityAttributeValues: null,
    filteredProduct: null,
    marketPriceDriverCombinations: [],
    priceBasisId: null,
    currencyId: null,
    uomId: null,
    pricePrecision: 2,
    volumePrecision: 0,
    currencySymbol: "$",
  };

  async componentDidMount() {
    const { productId, market } = this.props;
    const { marketId } = market;

    const { fromMarketDate, toMarketDate } = this.getDefaultDateRange();

    const [
      venue,
      { originalQualityAttributeValues, filteredProduct },
      termSchedule,
      marketPriceDriverCombinations,
    ] = await Promise.all([
      getVenue(market.venueId),
      getFilteredProductForMarket(productId, marketId, toMarketDate),
      getTermSchedule(market.termScheduleId),
      getMarketPriceDriverCombinations(marketId).catch((error) => {
        console.error(
          "Error loading market price driver combinations:",
          error
        );
        return [];
      }),
    ]);

    const timeZone =
      venue.timeZone +
      " (" +
      Intl.DateTimeFormat("us", {
        timeZoneName: "short",
        timeZone: venue.timeZone,
      })
        .formatToParts()
        .find((i) => i.type === "timeZoneName").value +
      ")";

    this.setState({
      fromMarketDate: fromMarketDate,
      toMarketDate: toMarketDate,
      originalQualityAttributeValues: originalQualityAttributeValues,
      filteredProduct: filteredProduct,
      marketPriceDriverCombinations: marketPriceDriverCombinations || [],
      marketAttributes: market.marketAttributes,
      termScheduleId: market.termScheduleId,
      forwardReportingPeriodicity: termSchedule.forwardReportingPeriodicity,
      historicalReportingPeriodicity:
        termSchedule.historicalReportingPeriodicity,
      priceBasisId: market.marketAttributes[0].priceBasisId,
      currencyId: market.marketAttributes[0].currencyId,
      uomId: market.marketAttributes[0].uomId,
      pricePrecision: market.marketAttributes[0].pricePrecision ?? 2,
      volumePrecision: market.marketAttributes[0].volumePrecision ?? 0,
      currencySymbol: market.marketAttributes[0].currencySymbol ?? "$",
      marketDate: toMarketDate,
      timeZone: timeZone,
      isLoading: false,
    });

    fromMarketDate &&
      toMarketDate &&
      this.props.market &&
      termSchedule &&
      (await this.loadData(
        fromMarketDate,
        toMarketDate,
        termSchedule.historicalReportingPeriodicity,
        {
          priceBasisId: market.marketAttributes[0].priceBasisId,
          currencyId: market.marketAttributes[0].currencyId,
          uomId: market.marketAttributes[0].uomId,
        }
      ));
  }

  getDefaultDateRange() {
    const today = new Date();
    const fromDate = new Date(today.getFullYear(), today.getMonth(), 1);

    return {
      fromMarketDate: fromDate.toLocaleDateString("fr-CA"),
      toMarketDate: today.toLocaleDateString("fr-CA"),
    };
  }

  getMonthDateRange(monthValue) {
    const [year, month] = monthValue.split("-").map(Number);
    const fromDate = new Date(year, month - 1, 1);
    const lastDayOfMonth = new Date(year, month, 0);
    const today = new Date();
    const toDate =
      today.getFullYear() === year && today.getMonth() === month - 1
        ? today
        : lastDayOfMonth;

    return {
      fromMarketDate: fromDate.toLocaleDateString("fr-CA"),
      toMarketDate: toDate.toLocaleDateString("fr-CA"),
    };
  }

  parseMarketDate(marketDate) {
    const date = new Date(marketDate);
    date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
    return date;
  }

  async loadData(
    fromMarketDate,
    toMarketDate,
    historicalReportingPeriodicity = this.state.historicalReportingPeriodicity,
    marketAttributeFilters = {
      priceBasisId: this.state.priceBasisId,
      currencyId: this.state.currencyId,
      uomId: this.state.uomId,
    }
  ) {
    const requestId = this._lastDataLoadRequestId + 1;
    this._lastDataLoadRequestId = requestId;

    const fromDate = this.parseMarketDate(fromMarketDate);
    const toDate = this.parseMarketDate(toMarketDate);

    this.setState({
      fromDate: fromDate,
      toDate: toDate,
      marketDataIsLoading: true,
    });

    const { market } = this.props;
    const { marketId } = market;

    try {
      const marks = await this.loadMarks(
        marketId,
        fromDate,
        toDate,
        historicalReportingPeriodicity,
        marketAttributeFilters
      );

      if (this._lastDataLoadRequestId !== requestId) {
        return;
      }

      this.setState({
        marks: marks,
        marketDataIsLoading: false,
      });
    } catch (error) {
      console.error("Error loading aggregate marks:", error);

      if (this._lastDataLoadRequestId !== requestId) {
        return;
      }

      this.setState({
        marks: [],
        marketDataIsLoading: false,
      });
    }
  }

  async loadMarks(
    marketId,
    fromDate,
    toDate,
    historicalReportingPeriodicity = this.state.historicalReportingPeriodicity,
    marketAttributeFilters = {
      priceBasisId: this.state.priceBasisId,
      currencyId: this.state.currencyId,
      uomId: this.state.uomId,
    }
  ) {
    const fromMarketDate = fromDate.toLocaleDateString("fr-CA");
    const toMarketDate = toDate.toLocaleDateString("fr-CA");
    const loadMarks =
      historicalReportingPeriodicity === HistoricalReportingPeriodicity.Year
        ? getInterpolatedAggregateMarksByMarket
        : getAggregateMarksByMarket;
    const marks = await loadMarks(marketId, fromMarketDate, toMarketDate, {
      markState: "Approved",
      ...marketAttributeFilters,
    });

    return marks;
  }

  handleFromMarketDateChange = async (fromMarketDate) => {
    this.setState({ fromMarketDate: fromMarketDate });

    const { toMarketDate } = this.state;
    await this.handleDateRangeChange(fromMarketDate, toMarketDate);
  };

  handleToMarketDateChange = async (toMarketDate) => {
    this.setState({
      marketDate: toMarketDate,
      toMarketDate: toMarketDate,
    });

    const { fromMarketDate } = this.state;
    await this.handleDateRangeChange(fromMarketDate, toMarketDate);
  };

  handleDateRangeChange = async (fromMarketDate, toMarketDate) => {
    const { market } = this.props;
    const { marketId } = market;
    const {
      historicalReportingPeriodicity,
      priceBasisId,
      currencyId,
      uomId,
    } = this.state;

    if (!fromMarketDate || !toMarketDate) {
      return;
    }

    // Perform refiltering asynchronously in background
    refilterProductForMarketDateChange(this, toMarketDate, marketId);

    await this.loadData(
      fromMarketDate,
      toMarketDate,
      historicalReportingPeriodicity,
      { priceBasisId, currencyId, uomId }
    );
  };

  handlemarketAttributeSelect = async (marketAttribute) => {
    const marketAttributeFilters = {
      priceBasisId: marketAttribute.priceBasisId,
      currencyId: marketAttribute.currencyId,
      uomId: marketAttribute.uomId,
    };

    this.setState({
      ...marketAttributeFilters,
    });

    const {
      fromMarketDate,
      toMarketDate,
      historicalReportingPeriodicity,
    } = this.state;

    if (fromMarketDate && toMarketDate) {
      await this.loadData(
        fromMarketDate,
        toMarketDate,
        historicalReportingPeriodicity,
        marketAttributeFilters
      );
    }
  };

  getFilteredMarks() {
    const { marks, priceBasisId, uomId, currencyId } = this.state;

    const filteredMarks = marks.filter(
      (x) =>
        x.priceBasisId === priceBasisId &&
        x.uomId === uomId &&
        x.currencyId === currencyId
    );

    return { marks: filteredMarks };
  }

  render() {
    const {
      timeZone,
      marketAttributes,
      termScheduleId,
      historicalReportingPeriodicity,
      isLoading,
      marketDataIsLoading,
      marketDate,
      toDate,
      fromDate,
      fromMarketDate,
      toMarketDate,
      priceBasisId,
      currencyId,
      uomId,
      pricePrecision,
      volumePrecision,
      currencySymbol,
      filteredProduct,
      marketPriceDriverCombinations,
    } = this.state;

    const product = filteredProduct;
    const { marks } = this.getFilteredMarks();

    if (isLoading || !fromDate || !toDate) return <Loading />;

    const { t, market } = this.props;
    const { marketId } = market;
    const marketName = (market?.name ?? market?.marketName ?? "").trim();
    const renderSectionTitle = (label) => (
      <span className="section-title-label">
        {label}
      </span>
    );

    const endDate = new Date().toLocaleDateString("fr-CA");

    return (
      <div className="container-fluid market-data-page">
        {marketName && <h1 className="market-name-page-title">{marketName}</h1>}
        <div className="row">
          <div className="col-md-3"></div>
          <div className="col-md-6">
            <div className="selector-title-class">{t("PriceBasis")}</div>
            <ListGroup
              value={marketAttributes[0]}
              horizontal="sm"
              className="price-basis-selector"
            >
              {marketAttributes.map((attribute, index) => (
                <ListGroup.Item
                  className="market-attribute-selector"
                  key={index}
                  action
                  active={priceBasisId === attribute.priceBasisId}
                  onClick={() => this.handlemarketAttributeSelect(attribute)}
                >
                  {attribute.priceBasisName +
                    " (" +
                    attribute.currencyName +
                    "/" +
                    attribute.uomName +
                    ")"}
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
          <div className="col-md-3"></div>
        </div>
        <div className="row align-items-end">
          <div className="col-auto">
            <Input
              type="date"
              max={toMarketDate}
              onChange={(e) =>
                this.handleFromMarketDateChange(e.currentTarget.value)
              }
              value={fromMarketDate}
              label={
                <span className="selector-title-class">{t("FromDate")}</span>
              }
            />
          </div>
          <div className="col-auto">
            <Input
              type="date"
              min={fromMarketDate}
              max={endDate}
              onChange={(e) =>
                this.handleToMarketDateChange(e.currentTarget.value)
              }
              value={toMarketDate}
              label={
                <span className="selector-title-class">{t("ToDate")}</span>
              }
            />
          </div>
          <div className="col"></div>
          <div className="col-auto align-self-start time-zone-block">
            <div className="selector-title-class time-zone-title">
              {t("TimeZone")}
            </div>
            <div>{timeZone}</div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-12 tile section-tile shadow-card">
            <h5 className="section-title">
              {renderSectionTitle(t("MarketDataHistory"))}
            </h5>
            {marketDataIsLoading ? (
              <Loading />
            ) : (
              <HistoricalMarketDataTable
                marks={marks}
                product={product}
                marketPriceDriverCombinations={marketPriceDriverCombinations}
                marketId={marketId}
                marketDate={marketDate}
                termScheduleId={termScheduleId}
                historicalReportingPeriodicity={historicalReportingPeriodicity}
                fromDate={fromDate}
                toDate={toDate}
                priceBasisId={priceBasisId}
                currencyId={currencyId}
                uomId={uomId}
                pricePrecision={pricePrecision}
                volumePrecision={volumePrecision}
                currencySymbol={currencySymbol}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default withTranslation(["marketdata"])(HistoricalMarketDataHeader);
